
import React, { useState, useEffect, useCallback, useMemo, useRef, useContext } from 'react';
import { generateHotelPath } from '../utils/slugUtils';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { SearchWidget } from './SearchWidget';
import { getRandomImageForTerm } from '../services/imageService';
import { searchTourvisio, getProductInfo } from '../services/tourvisioService';
import type { SearchQuery, ApiLogEntry, Language, DestinationContent, Hotel, WidgetSettings } from '../types';
import { SearchType } from '../types';
import { useLocalization } from '../hooks/useLocalization';
import { usePageContent } from '../hooks/usePageContent';
import { getCachedImageMap, saveCachedImageMap, getWidgetSettings, getHomepageAds } from '../services/dataService';
import PageCTA from './PageCTA';
import { subscribeToNewsletter } from '../services/mailchimpService';

interface MainSearchProps {
    language: Language;
    logApiCall: (log: ApiLogEntry) => void;
    isSettingsReady: boolean;
}

const FeatureIcon: React.FC<{ icon: React.ReactNode, title: string, desc: string }> = ({ icon, title, desc }) => (
    <div className="flex flex-col items-center text-center p-6 bg-white rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-50 group h-full justify-center">
        <div className="w-16 h-16 bg-gray-100 text-black rounded-full flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
            {icon}
        </div>
        <h3 className="text-lg font-bold text-gray-800 mb-2">{title}</h3>
        <p className="text-sm text-gray-500 leading-relaxed">{desc}</p>
    </div>
);

export const MainSearch: React.FC<MainSearchProps> = ({ logApiCall, isSettingsReady }) => {
    const { t, language } = useLocalization();
    const { content: cmsContent } = usePageContent('home', language);
    const navigate = useNavigate();
    const { state } = useLocation(); // Retrieve restoration state
    const BEACH_FALLBACK = 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?q=80&w=2000&auto=format&fit=crop';
    const [heroImages, setHeroImages] = useState<string[]>([BEACH_FALLBACK]);
    const [currentSlide, setCurrentSlide] = useState(0);
    const [ctaImage, setCtaImage] = useState('https://images.unsplash.com/photo-1548574505-5e239809ee19?q=80&w=2000&auto=format&fit=crop');
    const [popularDestinations, setPopularDestinations] = useState<DestinationContent[]>([]);
    const [isDiscovering, setIsDiscovering] = useState(false);
    const [widgets, setWidgets] = useState<WidgetSettings>({});
    const [ads, setAds] = useState<any[]>([]);

    // Dynamic CTA data from Tourvisio cache
    const [ctaData, setCtaData] = useState<{
        hotelName: string;
        price: number;
        currency: string;
        board: string;
        imageUrl: string;
        checkIn: string;
        nights: number;
    } | null>(null);

    // Prevent multiple hero fetches
    const heroFetchedRef = useRef(false);

    // Newsletter state
    const [email, setEmail] = useState('');
    const [subStatus, setSubStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
    const [subMessage, setSubMessage] = useState('');

    const STOCKHOLM_DEPARTURE_ID = "10";
    const BODRUM_DESTINATION_ID = "55";

    // Derived value for backward compatibility
    const heroImage = heroImages[0] || '';

    const refreshData = useCallback(async () => {
        const settings = await getWidgetSettings();
        setWidgets(settings);

        // Handle Hero Image logic without loops
        if (!heroFetchedRef.current) {
            heroFetchedRef.current = true;
            if (settings.heroImage && settings.heroImage.length > 5) {
                setHeroImages([settings.heroImage]);
            }
            // If no admin heroImage is set, keep the beach fallback (already in state)
        } else if (settings.heroImage && settings.heroImage !== heroImage && settings.heroImage.length > 5) {
            // Update if admin changed setting while viewing
            setHeroImages([settings.heroImage]);
        }

        // DB-FIRST: Fetch active destinations from API (MongoDB)
        let cmsDestinations: any[] = [];
        try {
            const res = await fetch('/api/destinations?active=true&limit=12');
            const data = await res.json();
            if (data.success && data.destinations) {
                cmsDestinations = data.destinations.map((d: any) => {
                    // Extract first approved serpApiImage as fallback
                    const serpImage = (d.serpApiImages || []).find((img: any) => img.approved)?.url || '';
                    // Safely extract description string
                    const descSv = typeof d.description === 'object' ? d.description?.sv || d.description?.en || '' : '';
                    return {
                        id: d._id,
                        destinationId: d.tourvisioId || d._id,
                        slug: d.slug,
                        title: d.title || d.name,
                        titleSv: d.titleSv || d.title || d.name,
                        imageUrl: d.landingPage?.heroImage || d.imageUrl || d.heroImage || serpImage || '',
                        price: d.country || '',
                        type: d.country || '',
                        isActive: d.isActive,
                        isFeatured: d.featured,
                        landingPage: d.landingPage
                    };
                });
            }
        } catch (e) {
            console.warn('[MainSearch] Failed to fetch destinations from API:', e);
        }
        const cachedImages = getCachedImageMap();
        let imageMap: Record<string, string> = cachedImages || {};
        let hasChanges = false;

        const updatedDestinations = await Promise.all(cmsDestinations.map(async (dest) => {
            const DEFAULT_FALLBACK_ID = 'cc02fe5d8800'; // Unique ID part of yellow van image

            // Priority 1: Pre-defined static imageUrl in Destination object (from dataService.ts)
            // This ALWAYS takes precedence to avoid API dependency issues
            if (dest.imageUrl && dest.imageUrl.length > 20 && !dest.imageUrl.includes('placeholder') && !dest.imageUrl.includes(DEFAULT_FALLBACK_ID)) {
                // Update cache with the static URL if different
                if (imageMap[dest.id] !== dest.imageUrl) {
                    imageMap[dest.id] = dest.imageUrl;
                    hasChanges = true;
                }
                return dest;
            }

            // Priority 2: Valid cached image (User setting/Cache), BUT ignore known bad fallbacks
            if (imageMap[dest.id] && !imageMap[dest.id].includes(DEFAULT_FALLBACK_ID) && imageMap[dest.id].length > 20) {
                return { ...dest, imageUrl: imageMap[dest.id] };
            }

            // Priority 3: Random fetch (Unsplash/Pexels/Pixabay via imageService) - only if no static URL
            const randomUrl = await getRandomImageForTerm(`${dest.title} travel`);

            const finalUrl = (randomUrl && !randomUrl.includes(DEFAULT_FALLBACK_ID))
                ? randomUrl
                : 'https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?q=80&w=1200';

            // Only cache if valid and NOT default
            if (finalUrl && !finalUrl.includes(DEFAULT_FALLBACK_ID)) {
                imageMap[dest.id] = finalUrl;
                hasChanges = true;
            }

            return { ...dest, imageUrl: finalUrl };
        }));

        if (hasChanges || !cachedImages) {
            saveCachedImageMap(imageMap);
        }
        setPopularDestinations(updatedDestinations);

        // Update hero images slider from destinations
        // Filter valid images that are not empty/placeholder/local-only
        const destinationHeroImages = updatedDestinations
            .map(d => d.landingPage?.heroImage || d.imageUrl)
            .filter(img => {
                if (!img || img.length < 20) return false;
                if (img.includes('placeholder')) return false;
                // Exclude relative /api/uploads/ paths (they only work via backend proxy, not reliable for hero)
                if (img.startsWith('/api/') || img.startsWith('/uploads/')) return false;
                // Only allow absolute http(s) URLs for the hero slider
                if (!img.startsWith('http')) return false;
                return true;
            });

        // Only update if we have valid images, otherwise keep existing/fallback
        if (destinationHeroImages.length > 0) {
            setHeroImages(destinationHeroImages);
        }
        // If no destination images and current heroImages is empty, ensure we have fallback
        else if (heroImages.length === 0) {
            setHeroImages([BEACH_FALLBACK]);
        }
    }, []);

    // Trigger refresh when language or settings change, but refreshData itself is stable
    useEffect(() => {
        refreshData();
        window.addEventListener('tourvisio_data_update', refreshData);
        window.addEventListener('storage', refreshData);
        return () => {
            window.removeEventListener('tourvisio_data_update', refreshData);
            window.removeEventListener('storage', refreshData);
        };
    }, [refreshData, language]);

    // Auto-rotate hero images slider
    useEffect(() => {
        if (heroImages.length <= 1) return;
        const interval = setInterval(() => {
            setCurrentSlide((prev) => (prev + 1) % heroImages.length);
        }, 5000); // 5 seconds per slide
        return () => clearInterval(interval);
    }, [heroImages.length]);

    useEffect(() => {
        const fetchCtaBackgroundFromBodrumResult = async () => {
            if (!isSettingsReady) return;
            const futureDate = new Date();
            futureDate.setDate(futureDate.getDate() + 60);
            const checkInDate = futureDate.toISOString().split('T')[0];

            const query: SearchQuery = {
                type: SearchType.PackageTours,
                departureId: STOCKHOLM_DEPARTURE_ID,
                destinationId: BODRUM_DESTINATION_ID,
                checkIn: checkInDate,
                nights: 8,
                rooms: 1,
                nationality: 'SE',
                currency: 'EUR',
                adults: 2,
                children: 0,
                childrenAges: []
            };

            try {
                const results = await searchTourvisio(query, language, (log) => { });
                if (results && results.length > 0) {
                    const firstHotel = results[0] as Hotel;
                    if (firstHotel.imageUrl) {
                        setCtaImage(firstHotel.imageUrl);
                        return;
                    }
                }
            } catch (e) { }
            const img = await getRandomImageForTerm("Bodrum Turkey resort luxury pool");
            if (img) setCtaImage(img);
        }
        fetchCtaBackgroundFromBodrumResult();
    }, [isSettingsReady, language]);

    // Fetch cached CTA data from backend
    useEffect(() => {
        const fetchCtaData = async () => {
            try {
                const response = await fetch('/api/cta/blue-dreams');
                const data = await response.json();
                if (data.success && data.data) {
                    setCtaData({
                        hotelName: data.data.hotelName || 'Blue Dreams Resort',
                        price: data.data.price || 0,
                        currency: data.data.currency || 'SEK',
                        board: data.data.board || 'Ultra All Inclusive',
                        imageUrl: data.data.imageUrl || '',
                        checkIn: data.data.checkIn || '2026-06-01',
                        nights: data.data.nights || 7
                    });
                    // Also update CTA image if available
                    if (data.data.imageUrl) {
                        setCtaImage(data.data.imageUrl);
                    }
                }
            } catch (error) {
                console.warn('Failed to fetch CTA data:', error);
            }
        };
        fetchCtaData();
    }, []);

    const handleSearch = (query: SearchQuery) => {
        navigate('/results', { state: { searchQuery: query, from: '/' } });
    };

    const handleDiscoverOffer = async () => {
        if (isDiscovering) return;
        setIsDiscovering(true);

        // 1. Priority: Explicit Link URL
        if (widgets.flashSaleLinkUrl) {
            if (widgets.flashSaleLinkUrl.startsWith('http')) {
                window.location.href = widgets.flashSaleLinkUrl;
            } else {
                navigate(widgets.flashSaleLinkUrl);
            }
            setIsDiscovering(false);
            return;
        }

        // 2. Priority: Destination Target
        const isDestinationTarget = widgets.flashSaleTargetType === 'destination';
        if (isDestinationTarget && widgets.flashSaleDestinationId) {
            // Either go to destination page or results? Assuming destination page exists.
            navigate(`/destination/${widgets.flashSaleDestinationId}`);
            setIsDiscovering(false);
            return;
        }

        // 3. Priority: Hotel Target or Legacy logic
        const TARGET_HOTEL_ID = widgets.flashSaleHotelId || widgets.ctaBannerHotelId || '101083'; // Explicitly set to Blue Dreams Resort ID as fallback
        const CAMPAIGN_CHECKIN = widgets.flashSaleStartDate || "2026-06-01"; // Matched to UI date "JUN '26" or dynamic
        const CAMPAIGN_NIGHTS = widgets.ctaBannerNights || 7;
        const CAMPAIGN_DEPARTURE = widgets.ctaBannerDepartureId || "10";
        const TARGET_DESTINATION_ID = widgets.flashSaleDestinationId || widgets.ctaBannerDestinationId || BODRUM_DESTINATION_ID;

        const query: SearchQuery = {
            type: SearchType.PackageTours,
            departureId: CAMPAIGN_DEPARTURE,
            departureName: widgets.ctaBannerDeparture || "Stockholm",
            departureType: 2,
            destinationId: TARGET_DESTINATION_ID,
            destinationName: widgets.ctaBannerDestination || "Bodrum",
            destinationType: 2,
            hotelId: TARGET_HOTEL_ID,
            checkIn: CAMPAIGN_CHECKIN,
            nights: CAMPAIGN_NIGHTS,
            rooms: 1,
            nationality: 'SE',
            currency: 'SEK',
            adults: 2,
            children: 0,
            childrenAges: []
        };

        try {
            const results = await searchTourvisio(query, language, logApiCall);
            if (results && results.length > 0) {
                const hotel = results[0] as Hotel;
                navigate(generateHotelPath(hotel), { state: { hotel, searchQuery: query } });
            } else {
                const staticInfo = await getProductInfo(2, TARGET_HOTEL_ID, 1, 'en-US', (log) => { });
                const fallbackImages = staticInfo?.images || [];
                const fallbackFacilities = staticInfo?.facilities || [];
                const fallbackHotel: Hotel = {
                    id: TARGET_HOTEL_ID,
                    productId: TARGET_HOTEL_ID,
                    provider: 1,
                    name: widgets.flashSaleHotelName || widgets.ctaBannerHotelName?.en || "Blue Dreams Resort", // Explicit name
                    location: { country: "Turkey", city: "Bodrum" },
                    rating: 5,
                    pricePerNight: 0,
                    currency: 'SEK',
                    imageUrl: fallbackImages[0] || ctaImage,
                    thumbnailUrl: fallbackImages[0] || ctaImage,
                    board: "Ultra All Inclusive",
                    roomType: "Standard Room",
                    themes: ["Luxury", "Beach", "Spa"],
                    facilities: fallbackFacilities,
                    offers: []
                };
                navigate(generateHotelPath(fallbackHotel), { state: { hotel: fallbackHotel, searchQuery: query } });
            }
        } catch (e) {
            // If direct fetch fails, try to fallback to a search result list but pre-filter for it
            navigate('/results', { state: { searchQuery: query } });
        } finally {
            setIsDiscovering(false);
        }
    };

    const handleSubscribe = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!email) return;
        setSubStatus('loading');

        const result = await subscribeToNewsletter(email);
        if (result.success) {
            setSubStatus('success');
            setSubMessage(result.message);
            setEmail('');
        } else {
            setSubStatus('error');
            setSubMessage(result.message);
        }
    };

    return (
        <div className="relative bg-gray-50 font-sans">
            {/* Immersive Hero Section */}
            <div className="relative h-[85vh] min-h-[600px] w-full overflow-hidden">
                <div className="absolute inset-0">
                    {heroImages.map((img, idx) => (
                        <img
                            key={idx}
                            src={img}
                            className={`absolute w-full h-full object-cover transform scale-105 transition-opacity duration-1000 ${idx === currentSlide ? 'opacity-100' : 'opacity-0'}`}
                            alt={`Destination ${idx + 1}`}
                        />
                    ))}
                    <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/20 to-gray-50/90"></div>
                </div>
                <div className="relative container mx-auto px-4 sm:px-6 lg:px-8 h-full flex flex-col justify-center items-center text-center pt-12 pb-32">
                    <span className="inline-block py-1 px-3 rounded-full bg-white/20 backdrop-blur-md border border-white/30 text-white text-xs font-bold uppercase tracking-widest mb-6 animate-fade-in-up">
                        {(language === 'en' ? cmsContent?.hero?.badge : null) || t('hero_badge')}
                    </span>
                    <h1 className="text-5xl md:text-7xl font-black text-white mb-4 leading-tight tracking-tight animate-fade-in-up delay-100">
                        {(language === 'en' ? cmsContent?.hero?.title : null) || (language === 'en' ? widgets.heroTitle1 : null) || t('hero_title_1')}<br />
                        {/* If widget title 2 exists and no CMS title, show it. CMS title usually covers full main text */}
                        {!(language === 'en' ? cmsContent?.hero?.title : null) && <span className="text-white/90">{(language === 'en' ? widgets.heroTitle2 : null) || t('hero_title_2')}</span>}
                    </h1>
                    <p className="text-lg md:text-xl text-gray-200 max-w-3xl font-light animate-fade-in-up delay-200">
                        {(language === 'en' ? cmsContent?.hero?.subtitle : null) || (language === 'en' ? widgets.heroSubtitle : null) || t('hero_subtitle')}
                    </p>
                </div>
            </div>

            {/* Search Widget */}
            <div className="relative z-20 -mt-[92px] mb-20 animate-fade-in-up delay-300">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <SearchWidget
                        onSearch={handleSearch}
                        logApiCall={logApiCall}
                        isSettingsReady={isSettingsReady}
                        initialQuery={state?.restoreQuery}
                    />
                </div>
            </div>

            {/* Unified Promo Section (Replaces Hardcoded Flash Sale) */}
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 mb-24">
                {ads && ads.length > 0 ? (
                    <PageCTA cta={ads[0]} language={language} variant="slim" />
                ) : widgets.flashSaleActive ? (
                    /* Legacy Fallback if no unified ads found but flash sale active */
                    <div
                        onClick={handleDiscoverOffer}
                        className="w-full bg-[#0F172A] rounded-xl overflow-hidden shadow-2xl flex flex-col md:flex-row items-center p-1 md:p-2 cursor-pointer group border border-slate-800 relative h-auto md:h-20"
                    >
                        {/* Background Image Absolute */}
                        <div className="absolute inset-0 z-0">
                            <img src={widgets.flashSaleImageUrl || ctaImage} className="w-full h-full object-cover opacity-30 mix-blend-luminosity group-hover:scale-105 transition-transform duration-700 ease-out" alt="Flash Sale" />
                            <div className="absolute inset-0 bg-gradient-to-r from-[#0F172A] via-[#0F172A]/90 to-transparent"></div>
                        </div>

                        <div className="relative z-10 flex flex-1 items-center justify-between w-full px-2 md:px-4 py-3 md:py-0">
                            {/* Left: Date + Content */}
                            <div className="flex items-center gap-4 md:gap-5 w-full md:w-auto">
                                {/* Date Widget */}
                                <div className="hidden md:flex flex-col items-center justify-center bg-[#1E293B] rounded-lg w-[58px] h-[58px] border border-slate-700 shadow-md shrink-0">
                                    <span className="text-lg font-black text-white leading-none tracking-tight">01</span>
                                    <span className="text-[9px] font-bold text-slate-400 uppercase mt-0.5">JUN '26</span>
                                </div>

                                {/* Text Info */}
                                <div className="flex flex-col">
                                    <div className="flex flex-wrap items-center gap-2 md:gap-3 mb-1">
                                        {(language === 'sv' ? widgets.flashSaleTitle?.sv : widgets.flashSaleTitle?.en) ? (
                                            <span className="bg-amber-500 text-white text-[9px] font-extrabold px-1.5 py-0.5 rounded-[3px] uppercase tracking-wider shadow-sm">FLASH SALE</span>
                                        ) : (
                                            <span className="bg-amber-500 text-white text-[9px] font-extrabold px-1.5 py-0.5 rounded-[3px] uppercase tracking-wider shadow-sm">EXCLUSIVE</span>
                                        )}
                                        <h3 className="text-lg md:text-xl font-bold text-white tracking-tight leading-none shadow-black drop-shadow-md">
                                            {language === 'sv' && widgets.flashSaleTitle?.sv ? widgets.flashSaleTitle.sv : widgets.flashSaleTitle?.en}
                                            {!(widgets.flashSaleTitle?.en || widgets.flashSaleTitle?.sv) && (
                                                widgets.flashSaleHotelName || widgets.flashSaleDestinationName || widgets.ctaBannerHotelName?.[language] || widgets.ctaBannerHotelName?.en || ctaData?.hotelName || 'Blue Dreams Resort'
                                            )}
                                        </h3>
                                    </div>
                                    <div className="flex flex-wrap items-center text-[10px] md:text-xs text-slate-300 font-medium gap-2">
                                        <span className="text-amber-400 font-bold">
                                            {language === 'sv' && widgets.flashSaleDescription?.sv ? widgets.flashSaleDescription.sv : widgets.flashSaleDescription?.en}
                                            {!(widgets.flashSaleDescription?.en || widgets.flashSaleDescription?.sv) && (
                                                `Departure: ${widgets.ctaBannerDeparture || 'Stockholm'}`
                                            )}
                                        </span>
                                        {!(widgets.flashSaleDescription?.en || widgets.flashSaleDescription?.sv) && (
                                            <>
                                                <span className="w-1 h-1 bg-slate-600 rounded-full hidden sm:block"></span>
                                                <span className="hidden sm:block">{widgets.ctaBannerDestination || 'Bodrum, Turkey'}</span>
                                            </>
                                        )}
                                        {!(widgets.flashSaleDescription?.en || widgets.flashSaleDescription?.sv) && (
                                            <>
                                            <span className="w-1 h-1 bg-slate-600 rounded-full hidden sm:block"></span>
                                            <span className="hidden sm:block opacity-80">{widgets.ctaBannerBoard || ctaData?.board || 'Ultra All Inclusive'}</span>
                                            </>
                                        )}
                                        {ctaData?.price && !(widgets.flashSaleDescription?.en || widgets.flashSaleDescription?.sv) ? (
                                            <>
                                                <span className="w-1 h-1 bg-slate-600 rounded-full hidden sm:block"></span>
                                                <span className="text-amber-400 font-bold hidden sm:block">från {ctaData.price.toLocaleString()} {ctaData.currency}</span>
                                            </>
                                        ) : null}
                                    </div>
                                </div>
                            </div>

                            {/* Right: Button */}
                            <div className="flex-shrink-0 pl-4 mt-2 md:mt-0 ml-auto md:ml-0">
                                <button
                                    className="bg-white hover:bg-slate-50 text-slate-900 px-5 py-2.5 rounded-full font-bold text-xs md:text-sm flex items-center gap-2 transition-all shadow-xl hover:shadow-2xl transform hover:-translate-y-0.5"
                                    disabled={isDiscovering}
                                >
                                    {isDiscovering ? (
                                        <span className="flex items-center gap-2">
                                            <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                                            Processing...
                                        </span>
                                    ) : (
                                        <>
                                            <span className="whitespace-nowrap">Check Availability</span>
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" /></svg>
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                ) : null}
            </div>


            {/* Popular Destinations */}
            <div className="py-24 bg-white border-t border-gray-100">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <span className="text-black font-bold uppercase tracking-wider text-xs">{t('inspiration')}</span>
                        <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mt-2">{t('popular_destinations')}</h2>
                        <p className="text-gray-500 mt-4 max-w-2xl mx-auto text-lg">{t('popular_subtitle')}</p>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                        {popularDestinations.map((dest, idx) => (
                            <Link to={`/destination/${(dest as any).slug || dest.destinationId || dest.id}`} key={idx} className="bg-white border border-gray-100 rounded-2xl overflow-hidden hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 group cursor-pointer block">
                                <div className="h-64 overflow-hidden relative">
                                    <img src={dest.imageUrl} alt={dest.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                                    <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm px-3 py-1.5 rounded-lg text-sm font-bold text-gray-900 shadow-md">{dest.price}</div>
                                    <div className="absolute inset-0 bg-black/20 group-hover:bg-black/10 transition-colors"></div>
                                </div>
                                <div className="p-6">
                                    <h3 className="font-bold text-xl text-gray-900 leading-tight mb-2 flex items-center justify-between">
                                        {language === 'sv' ? (dest.titleSv || dest.title) : language === 'da' ? (dest.titleSv || dest.title) : dest.title}
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-black opacity-0 group-hover:opacity-100 transition-opacity transform translate-x-2 group-hover:translate-x-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" /></svg>
                                    </h3>
                                    <span className="text-xs font-bold text-black bg-gray-100 px-2 py-1 rounded-full uppercase tracking-wider">{dest.type}</span>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </div>

            {/* Newsletter Section */}
            <div className="py-20 bg-gradient-to-br from-blue-900 via-blue-800 to-teal-700">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                        {language === 'sv' ? 'Håll dig uppdaterad' : language === 'da' ? 'Hold dig opdateret' : 'Stay Updated'}
                    </h2>
                    <p className="text-blue-100 mb-8 max-w-xl mx-auto">
                        {language === 'sv'
                            ? 'Få de senaste resenyheterna och erbjudandena direkt till din inkorg.'
                            : language === 'da'
                            ? 'Få de seneste rejsenyheder og tilbud direkte i din indbakke.'
                            : 'Get the latest travel news and offers delivered to your inbox.'}
                    </p>
                    <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-4 justify-center max-w-lg mx-auto">
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder={language === 'sv' ? 'Din e-postadress' : language === 'da' ? 'Din e-mailadresse' : 'Your email address'}
                            className="flex-1 px-5 py-3 rounded-full text-gray-900 bg-white focus:ring-2 focus:ring-amber-400 outline-none"
                            required
                        />
                        <button
                            type="submit"
                            disabled={subStatus === 'loading'}
                            className="bg-amber-500 hover:bg-amber-600 text-white font-bold px-8 py-3 rounded-full transition-all shadow-lg disabled:opacity-50"
                        >
                            {subStatus === 'loading'
                                ? '...'
                                : (language === 'sv' ? 'Prenumerera' : language === 'da' ? 'Abonner' : 'Subscribe')}
                        </button>
                    </form>
                    {subStatus === 'success' && <p className="text-green-300 mt-4">{subMessage}</p>}
                    {subStatus === 'error' && <p className="text-red-300 mt-4">{subMessage}</p>}
                </div>
            </div>

            {/* Membership / Lumi Privé Section - Removed as per user request */}
            {widgets.showMembershipSection && (
                <div className="w-full py-24 bg-gradient-to-br from-amber-50 via-orange-50/30 to-white relative overflow-hidden border-t border-amber-100/50">
                    {/* Content hidden by default, can be re-enabled via widgets if needed later */}
                </div>
            )}

        </div>
    );
};
